/**
 * @fileoverview useMonthlyData - Monthly transaction data management hook.
 *
 * Manages transaction loading and caching with a "single active month" strategy.
 * Only the currently viewed month's transactions are kept in memory, reducing
 * memory usage and improving performance.
 *
 * @description This hook provides:
 * - Automatic transaction loading when month changes
 * - Available months tracking (months with data)
 * - Transaction CRUD operations with API sync
 * - Smart cache invalidation on budget context change
 * - Month key conversion utilities (Date ↔ YYYY-MM)
 *
 * Data loading strategy:
 * - Only one month's transactions in memory at a time
 * - Automatically loads when selectedMonth changes
 * - Clears cache when budget context changes
 * - Tracks which months have transaction data
 *
 * Month key format: YYYY-MM (e.g., "2025-10")
 *
 * Transaction operations automatically:
 * - Update local state optimistically
 * - Sync with backend via API
 * - Handle month changes (moving transactions between months)
 * - Refresh available months list
 *
 * @module hooks/useMonthlyData
 */
import { useState, useEffect, useCallback } from 'react';
import { transactionAPI, onBudgetContextChange } from '../services/api';
import { useRealtime } from '../contexts/RealtimeContext';

/**
 * Hook for managing monthly transaction data.
 *
 * @param {Date} selectedMonth - Currently selected month
 * @param {number} currentUserId - Current user's ID for filtering own actions in realtime updates
 * @returns {Object} Monthly data and operations
 * @returns {Array} return.activeTransactions - Transactions for current month
 * @returns {Function} return.setActiveTransactions - Manually set transactions
 * @returns {string|null} return.loadedMonth - Month key currently loaded
 * @returns {boolean} return.isLoading - Whether data is loading
 * @returns {Array<string>} return.availableMonths - Months with transaction data
 * @returns {Function} return.hasDataForMonth - Check if month has data
 * @returns {Function} return.addTransactionToMonth - Add transaction
 * @returns {Function} return.deleteTransactionFromMonth - Delete transaction
 * @returns {Function} return.updateTransactionInMonth - Update transaction
 * @returns {Function} return.getMonthKey - Convert Date to YYYY-MM string
 * @returns {Function} return.loadMonthData - Manually load month data
 *
 * @example
 * const monthlyData = useMonthlyData(selectedMonth);
 * console.log(monthlyData.activeTransactions); // Transactions for current month
 * await monthlyData.addTransactionToMonth(newTransaction);
 */
export const useMonthlyData = (selectedMonth, currentUserId = null) => {
    // Active month data (only current month in memory)
    const [activeTransactions, setActiveTransactions] = useState([]);
    const [loadedMonth, setLoadedMonth] = useState(null);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Realtime context for live updates
    // Include isConnected so we re-subscribe when socket connects
    // getSocketId used to filter own actions by socket ID (enables same-user multi-device sync)
    const { subscribeToEvent, updateMonthView, isConnected, getSocketId } = useRealtime();

    // Generate month key from Date object (YYYY-MM format)
    const getMonthKey = useCallback((date) => {
        if (typeof date === 'string') {
            // If it's a date string like "2025-08-15", extract year-month
            const [year, month] = date.split('-');
            return `${year}-${month}`;
        }

        if (date instanceof Date) {
            // If it's a Date object, format it
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${year}-${month}`;
        }

        // Error handling - invalid date format
        return null;
    }, []);

    // Get current month key from selectedMonth
    const currentMonthKey = getMonthKey(selectedMonth);


    // Load transactions for a specific month from API
    const loadMonthData = useCallback(async (monthKey) => {
        if (!monthKey) return [];

        setIsLoading(true);

        try {
            const monthTransactions = await transactionAPI.getByMonth(monthKey);
            setActiveTransactions(monthTransactions);
            setLoadedMonth(monthKey);

            return monthTransactions;
        } catch (error) {
            // Silently fail if authentication isn't ready or no budget context
            // Error handling
            setActiveTransactions([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load available months from API
    const loadAvailableMonths = useCallback(async () => {
        try {
            const months = await transactionAPI.getAvailableMonths();
            setAvailableMonths(months);
        } catch (error) {
            // Silently fail if authentication isn't ready or no budget context
            // Error handling
        }
    }, []);


    // Add transaction via API
    const addTransactionToMonth = useCallback(async (transaction) => {
        try {
            const newTransaction = await transactionAPI.create(transaction);

            const transactionMonthKey = getMonthKey(newTransaction.date);

            // If it's for the current month, add to active transactions
            // Use duplicate check to handle race condition where socket event may arrive
            // before HTTP response (socket broadcast is sent before response in backend)
            if (transactionMonthKey === currentMonthKey) {
                setActiveTransactions(prev => {
                    if (prev.some(t => String(t.id) === String(newTransaction.id))) return prev;
                    return [...prev, newTransaction];
                });
            }

            // Refresh available months
            await loadAvailableMonths();

            return newTransaction;
        } catch (error) {
            // Error handling - parse error response
            const errorMessage = error.message || 'Failed to add transaction';
            throw new Error(errorMessage);
        }
    }, [currentMonthKey, getMonthKey, loadAvailableMonths]);

    // Delete transaction via API
    const deleteTransactionFromMonth = useCallback(async (transactionId) => {
        try {
            await transactionAPI.delete(transactionId);

            setActiveTransactions(prev => prev.filter(t => t.id !== transactionId));

        } catch (error) {
            // Error handling
            throw new Error(error.message || 'Failed to delete transaction');
        }
    }, []);

    // Update transaction via API
    const updateTransactionInMonth = useCallback(async (updatedTransaction) => {
        try {
            const result = await transactionAPI.update(updatedTransaction.id, updatedTransaction);

            const newMonthKey = getMonthKey(result.date);

            if (newMonthKey !== currentMonthKey) {
                // Transaction moved to different month - remove from view
                setActiveTransactions(prev => prev.filter(t => t.id !== result.id));
            } else {
                // Update in current month
                setActiveTransactions(prev =>
                    prev.map(t => (t.id === result.id ? result : t))
                );
            }

            return result;
        } catch (error) {
            // Re-throw ConflictError directly to preserve its properties for conflict resolution
            // Other errors get wrapped with a user-friendly message
            if (error.isConflict || error.name === 'ConflictError') {
                throw error;
            }
            throw new Error(error.message || 'Failed to update transaction');
        }
    }, [currentMonthKey, getMonthKey]);

    // Check if a month has data
    const hasDataForMonth = useCallback((monthKey) => {
        return availableMonths.includes(monthKey);
    }, [availableMonths]);


    // Load month data when selectedMonth changes
    useEffect(() => {
        if (currentMonthKey && currentMonthKey !== loadedMonth) {
            loadMonthData(currentMonthKey);
        }
    }, [currentMonthKey, loadedMonth, loadMonthData]);

    // Load available months on first load
    useEffect(() => {
        loadAvailableMonths();
    }, [loadAvailableMonths]);

    // Listen for budget context changes and reset loaded month
    useEffect(() => {
        const unsubscribe = onBudgetContextChange(() => {
            setLoadedMonth(null); // Reset so data reloads for new budget
            setActiveTransactions([]); // Clear old transactions immediately
            loadAvailableMonths(); // Reload available months for new budget
        });

        return () => unsubscribe();
    }, [loadAvailableMonths]);

    // Notify server when month view changes (for presence tracking)
    useEffect(() => {
        if (currentMonthKey && updateMonthView) {
            updateMonthView(currentMonthKey);
        }
    }, [currentMonthKey, updateMonthView]);

    // Subscribe to realtime transaction events
    useEffect(() => {
        if (!subscribeToEvent || !isConnected) return;

        // Handle transaction created by another user/device
        const unsubscribeCreated = subscribeToEvent('transaction:created', (data) => {
            const { transaction, sourceSocketId } = data;

            // Skip if this event came from our own socket (we already added it optimistically)
            // Using socket ID instead of userId allows same-user multi-device sync
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            // Only add if it's for the currently viewed month
            const transactionMonthKey = getMonthKey(transaction.date);
            if (transactionMonthKey === currentMonthKey) {
                setActiveTransactions(prev => {
                    // Avoid duplicates - use String() for type-safe comparison
                    if (prev.some(t => String(t.id) === String(transaction.id))) return prev;
                    return [...prev, transaction];
                });
            }

            // Refresh available months in case this created a new month
            loadAvailableMonths();
        });

        // Handle transaction updated by another user/device
        const unsubscribeUpdated = subscribeToEvent('transaction:updated', (data) => {
            const { transaction, sourceSocketId } = data;

            // Skip if this event came from our own socket
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            const newMonthKey = getMonthKey(transaction.date);

            if (newMonthKey === currentMonthKey) {
                // Transaction is in current month - update or add it
                setActiveTransactions(prev => {
                    // Use String() for type-safe comparison (IDs may be string or number)
                    const existingIndex = prev.findIndex(t => String(t.id) === String(transaction.id));
                    if (existingIndex >= 0) {
                        // Update existing transaction
                        const updated = [...prev];
                        updated[existingIndex] = transaction;
                        return updated;
                    } else {
                        // Transaction was moved INTO this month from another month
                        return [...prev, transaction];
                    }
                });
            } else {
                // Transaction was moved OUT of current month - remove it
                setActiveTransactions(prev =>
                    prev.filter(t => String(t.id) !== String(transaction.id))
                );
            }
        });

        // Handle transaction deleted by another user/device
        const unsubscribeDeleted = subscribeToEvent('transaction:deleted', (data) => {
            const { transactionId, sourceSocketId } = data;

            // Skip if this event came from our own socket
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            setActiveTransactions(prev => {
                // Use String() for type-safe comparison (transaction IDs may be string or number)
                const filtered = prev.filter(t => String(t.id) !== String(transactionId));
                return filtered;
            });
        });

        return () => {
            unsubscribeCreated();
            unsubscribeUpdated();
            unsubscribeDeleted();
        };
    // isConnected in deps ensures we re-subscribe when socket connects
    // getSocketId used for filtering own actions (same-user multi-device sync)
    }, [subscribeToEvent, currentMonthKey, getSocketId, getMonthKey, loadAvailableMonths, isConnected]);

    return {
        // Current month data
        activeTransactions,
        setActiveTransactions,
        loadedMonth,
        isLoading,

        // Month navigation info
        availableMonths,
        hasDataForMonth,

        // Transaction operations
        addTransactionToMonth,
        deleteTransactionFromMonth,
        updateTransactionInMonth,

        // Utility functions
        getMonthKey,

        // Manual operations
        loadMonthData
    };
};